import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import User from './api/User';
import { useKeycloak } from './KeycloakContext';

export const SessionContext = createContext({
    isAuthenticated: false,
    session: null,
    sessionLoaded: false,
    sessionError: null,
    refreshSession: async () => null,
});

export const useSession = () => {
    return useContext(SessionContext);
};

export const SessionProvider = ({ children }) => {
    const {
        initialized,
        authenticated,
        logout,
    } = useKeycloak();

    const [session, setSession] = useState(null);
    const [sessionLoaded, setSessionLoaded] = useState(false);
    const [sessionError, setSessionError] = useState(null);

    const saveSessionLocally = useCallback((sessionData) => {
        if (!sessionData) {
            return;
        }

        if (sessionData.id !== undefined && sessionData.id !== null) {
            localStorage.setItem(
                'userID',
                String(sessionData.id)
            );
        }

        if (sessionData.name) {
            localStorage.setItem('name', sessionData.name);
        }

        if (sessionData.email) {
            localStorage.setItem('email', sessionData.email);
        }
    }, []);

    const clearLocalSession = useCallback(() => {
        localStorage.removeItem('userID');
        localStorage.removeItem('name');
        localStorage.removeItem('email');

        setSession(null);
    }, []);

    const refreshSession = useCallback(async () => {
        if (!initialized || !authenticated) {
            setSession(null);
            setSessionLoaded(initialized);

            return null;
        }

        setSessionError(null);

        try {
            const response = await User.getSession();
            const sessionData = response.data;

            setSession(sessionData);
            saveSessionLocally(sessionData);

            return sessionData;
        } catch (error) {
            console.error(
                'Erro ao carregar a sessão do Carsharing:',
                error
            );

            setSession(null);
            setSessionError(error);

            const status = error?.response?.status;

            if (status === 401) {
                clearLocalSession();

                await logout({
                    redirectUri: window.location.origin,
                });
            }

            return null;
        } finally {
            setSessionLoaded(true);
        }
    }, [
        initialized,
        authenticated,
        logout,
        clearLocalSession,
        saveSessionLocally,
    ]);

    useEffect(() => {
        if (!initialized) {
            return;
        }

        if (!authenticated) {
            clearLocalSession();
            setSessionLoaded(true);

            return;
        }

        setSessionLoaded(false);
        refreshSession();
    }, [
        initialized,
        authenticated,
        clearLocalSession,
        refreshSession,
    ]);

    const contextValue = useMemo(() => ({
        isAuthenticated: authenticated,
        session,
        sessionLoaded,
        sessionError,
        refreshSession,
    }), [
        authenticated,
        session,
        sessionLoaded,
        sessionError,
        refreshSession,
    ]);

    return (
        <SessionContext.Provider value={contextValue}>
            {children}
        </SessionContext.Provider>
    );
};